import { LlmModelName } from 'llm-model-registry';
import { z } from 'zod';

import { HumanTaskStepSchema } from './taskConfig.js';
import { DEFAULT_ENABLED_HUMAN_TASK_TOOLS, HumanTaskToolsSchema } from './toolsConfig.js';
import { HumanTaskWorkContextSchema } from './workContextConfig.js';
import { Kind as RpaJobKind } from '../../rpaJob.js';
import {
  BaseFlowBlockSchema,
  BlockType,
  DurationUnits,
  ProducedType,
  returnTypeSchema,
} from '../base/baseSchema.js';
import {
  BlockValueFieldSchema,
  BlockValueFieldSchemaVariant,
} from '../base/blockValueFieldSchema.js';
import { BlockValueTemplateDurationSchema } from '../base/blockValueTemplateDurationSchema.js';
import { BlockValueUuidSchema } from '../base/blockValueUuidSchema.js';
import { TagNameSchema } from '../tag.js';

export enum HumanTaskPresentationMode {
  Standard = 'Standard',
  BrowserExtension = 'BrowserExtension',
}

export enum HumanTaskType {
  CustomSubtask = 'Custom',
  SubmitPriorAuthorization = 'SubmitPriorAuthorization',
  DynamicConfig = 'DynamicConfig',
}

export enum HumanTaskAutofillStrategy {
  Scripted = 'Scripted',
  AiAssisted = 'AiAssisted',
}

export enum AuthorizationStatusDetailsRequirement {
  Required = 'Required',
  Optional = 'Optional',
}

export const HumanTaskSubtaskSchema = z.object({
  id: z.string().uuid(),
  name: z.string().optional().default(''),
  steps: z.array(HumanTaskStepSchema),
});
export type HumanTaskSubtask = z.infer<typeof HumanTaskSubtaskSchema>;

const TagsExpressionSchema = BlockValueFieldSchema({
  variant: BlockValueFieldSchemaVariant.OptionalTemplate,
});

/** Array entries may be template placeholders; `TagSchema` is applied in `resolveTagsForConfig`. */
const HumanTaskTagsArraySchema = z
  .array(z.union([TagsExpressionSchema, TagNameSchema]))
  .refine((tags) => new Set(tags).size === tags.length, {
    message: 'All items must be unique, no duplicate values allowed',
  });

export const HumanTaskTagsConfigSchema = z.union([HumanTaskTagsArraySchema, TagsExpressionSchema]);

export const BaseHumanTaskBlockConfigSchema = z.object({
  // @deprecated Use subtasks instead
  steps: z.array(HumanTaskStepSchema).optional(),
  // FIXME: Move this field into CustomSubtaskBlockConfigSchema
  subtasks: z.array(HumanTaskSubtaskSchema).default([]),
  workContexts: z.array(HumanTaskWorkContextSchema).default([]),
  expirationDuration: BlockValueTemplateDurationSchema().default({
    value: '1',
    units: DurationUnits.Days,
  }),
  categoryId: BlockValueUuidSchema({ isRequired: false }).nullish(),
  externalId: z.string().nullish(),
  presentationMode: z.nativeEnum(HumanTaskPresentationMode).optional(),
  autofillStrategy: z.nativeEnum(HumanTaskAutofillStrategy).optional(),
  tools: HumanTaskToolsSchema.default({
    enabledTools: DEFAULT_ENABLED_HUMAN_TASK_TOOLS,
    config: {},
  }).optional(),
  workflowTransitionRpaJobKind: z.nativeEnum(RpaJobKind).optional(),
  tags: HumanTaskTagsConfigSchema.default([]),
});
export const CustomSubtaskBlockConfigSchema = BaseHumanTaskBlockConfigSchema.extend({
  taskType: z.literal(HumanTaskType.CustomSubtask),
});

export const HumanTaskDynamicConfigBlockConfigSchema = BaseHumanTaskBlockConfigSchema.extend({
  taskType: z.literal(HumanTaskType.DynamicConfig),
  dynamicConfig: BlockValueFieldSchema({
    variant: BlockValueFieldSchemaVariant.TemplateAndPlainString,
  }),
});
export type HumanTaskDynamicConfigBlockConfig = z.infer<
  typeof HumanTaskDynamicConfigBlockConfigSchema
>;

export const PriorAuthorizationInformationSchema = z.object({
  patientOrderId: z.string().min(1, { message: 'Patient order ID is required' }),
  renderingFacility: z.string().optional(),
  contactInformation: z.string().optional(),
  clinicalDocumentsPacket: z.string().optional(),
  copilotDataContext: z.string().optional(),
  copilotDataContextObject: z.string().optional(),
  modelName: z.nativeEnum(LlmModelName).optional(),
  predictionOptions: z
    .object({
      preamble: z.string().optional(),
      outputRequirement: z.string().optional(),
      outro: z.string().optional(),
    })
    .optional(),
});
export type PriorAuthorizationInformation = z.infer<typeof PriorAuthorizationInformationSchema>;

export const SubmitPriorAuthorizationBlockConfigSchema = BaseHumanTaskBlockConfigSchema.extend({
  taskType: z.literal(HumanTaskType.SubmitPriorAuthorization),
  priorAuthorizationInformation: PriorAuthorizationInformationSchema,
  /** Defaults to `Required` so existing configs without the field require status details. */
  authorizationStatusDetailsRequirement: z
    .nativeEnum(AuthorizationStatusDetailsRequirement)
    .default(AuthorizationStatusDetailsRequirement.Required),
  subtasks: z
    .array(HumanTaskSubtaskSchema.extend({ procedureId: z.string().uuid().optional() }))
    .default([]),
});
export type SubmitPriorAuthorizationBlockConfig = z.infer<
  typeof SubmitPriorAuthorizationBlockConfigSchema
>;

export const HumanTaskBlockConfigSchema = z.discriminatedUnion('taskType', [
  CustomSubtaskBlockConfigSchema,
  SubmitPriorAuthorizationBlockConfigSchema,
  HumanTaskDynamicConfigBlockConfigSchema,
]);
export type HumanTaskBlockConfig = z.infer<typeof HumanTaskBlockConfigSchema>;

export function isSubmitPriorAuthorizationBlockConfig(
  blockConfig: HumanTaskBlockConfig
): blockConfig is SubmitPriorAuthorizationBlockConfig {
  return blockConfig.taskType === HumanTaskType.SubmitPriorAuthorization;
}

export const HumanTaskBlockSchema = BaseFlowBlockSchema.extend({
  type: z.literal(BlockType.HumanTask),
  config: HumanTaskBlockConfigSchema,
  returnType: returnTypeSchema(ProducedType.HumanTask),
});
export type HumanTaskBlock = z.infer<typeof HumanTaskBlockSchema>;
